import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Params,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AccountsService } from '@services/accounts.service';
import { first, map } from 'rxjs/operators';

function isExperienceCreateUrl(segments: string[], queryParams: Params): boolean {
  const i = segments.indexOf('products');
  return i >= 0 && segments[i + 1] === 'new' && queryParams['fromExperience'] === 'true';
}

@Injectable({
  providedIn: 'root',
})
export class ProductCatalogAccessGuard implements CanLoad, CanActivate, CanActivateChild {
  constructor(private accounts: AccountsService, private router: Router) {}

  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    const segmentPaths = segments.map((s) => s.path);
    const queryParams = this.currentQueryParams();
    return this.checkAccess(segmentPaths, queryParams);
  }

  canActivate(
    _next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivateFromState(state);
  }

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivateFromState(state);
  }

  private canActivateFromState(
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const tree = this.router.parseUrl(state.url);
    const segments =
      tree.root.children['primary']?.segments.map((s) => s.path) ?? [];
    return this.checkAccess(segments, tree.queryParams);
  }

  private checkAccess(
    segments: string[],
    queryParams: Params
  ): Observable<boolean> {
    return this.accounts.company$.pipe(
      first(),
      map((company) => {
        if (company?.dws_pro_enabled) {
          return true;
        }
        if (isExperienceCreateUrl(segments, queryParams)) {
          return true;
        }
        return false;
      })
    );
  }

  private currentQueryParams(): Params {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extractedUrl?.queryParams) {
      return navigation.extractedUrl.queryParams;
    }
    return this.router.parseUrl(this.router.url).queryParams;
  }
}
